import { Component, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { filter } from 'rxjs';

interface MenuItem {
  label: string;
  icon: string;
  route: string;
  submenuKey?: string;
  /** The item is also active on every page below this path, e.g. all the screens of the Agents module. */
  activePrefix?: string;
}

interface SubmenuItem {
  label: string;
  route: string | null;
}

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [CommonModule, RouterLink],
  templateUrl: './sidebar.html',
  styleUrl: './sidebar.css'
})
export class Sidebar {
  menuItems: MenuItem[] = [
    { label: 'Dashboard', icon: '/assets/icons/dashboard.svg', route: '/dashboard' },
    { label: 'Guardrail Library', icon: '/assets/icons/guardrail.svg', route: '/guardrails', submenuKey: 'guardrails' },
    { label: 'Ingestion', icon: '/assets/icons/documents.svg', route: '/data-sources', submenuKey: 'dataSources' },
    { label: 'Connectors', icon: '/assets/icons/connectors.svg', route: '/connectors' },
    { label: 'Thread Activity', icon: '/assets/icons/thread-activity.svg', route: '/thread-activity' },
    { label: 'Activity Logs', icon: '/assets/icons/logs.svg', route: '/activity-logs' },
    { label: 'Classification', icon: '/assets/icons/classification.svg', route: '/documents/classified' },
    { label: 'Knowledge Graph', icon: '/assets/icons/knowledge-graph.svg', route: '/documents/knowledge-graph', submenuKey: 'knowledgeGraph' },
    { label: 'Policy & Flows', icon: '/assets/icons/knowledge-graph.svg', route: '/policy-flow' },
    { label: 'Brain Activity', icon: '/assets/icons/brain.svg', route: '/brain-activity' },
    { label: 'Tools', icon: '/assets/icons/brain.svg', route: '/tools' },
    { label: 'Agents', icon: '/assets/icons/agents.svg', route: '/agents/applications', activePrefix: '/agents' },
    { label: 'Prerequisites', icon: '/assets/icons/pre-req.svg', route: '/pre-req' },
    { label: 'Analytics', icon: '/assets/icons/analytics.svg', route: '/analytics' },
    { label: 'Failed Documents', icon: '/assets/icons/severity/critical.svg', route: '/dlq' },
    { label: 'Settings', icon: '/assets/icons/settings.svg', route: '/settings' }
  ];

  chevronIcon = '/assets/icons/chevron-right.svg';
  brainIcon = '/assets/icons/brain.svg';

  submenus: Record<string, SubmenuItem[]> = {
    guardrails: [
      { label: 'Business Guardrails', route: '/guardrails' },
      { label: 'LLM Powered', route: '/guardrail-config' }
    ],
    knowledgeGraph: [
      { label: 'Graph Explorer', route: '/documents/knowledge-graph' },
      { label: 'Playground', route: '/documents/knowledge-graph/playground' }
    ],
    dataSources: [
      { label: 'Data Sources', route: '/data-sources' },
      { label: 'Manage Batches', route: '/manage-batches' },
      { label: 'Version Review', route: '/version-review' },
      { label: 'Timeline', route: '/version-timeline' }
    ]
  };

  private openSubmenus = signal<Set<string>>(new Set());

  constructor(public router: Router) {
    this.syncOpenSubmenusWithRoute();

    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      takeUntilDestroyed()
    ).subscribe(() => {
      this.syncOpenSubmenusWithRoute();
    });
  }

  isActive(route: string): boolean {
    return this.router.url.split('?')[0] === route;
  }

  isItemActive(item: MenuItem): boolean {
    if (this.isActive(item.route)) return true;
    return !!item.activePrefix && (this.router.url.split('?')[0] + '/').startsWith(item.activePrefix + '/');
  }

  isSubmenuActive(key: string): boolean {
    return (this.submenus[key] || []).some(sub => !!sub.route && this.isActive(sub.route));
  }

  isSubmenuOpen(key: string): boolean {
    return this.openSubmenus().has(key);
  }

  toggleSubmenu(key: string) {
    this.openSubmenus.update(open => {
      const next = new Set(open);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  private syncOpenSubmenusWithRoute() {
    for (const key of Object.keys(this.submenus)) {
      if (this.isSubmenuActive(key)) {
        this.openSubmenus.update(open => new Set(open).add(key));
      }
    }
  }
}
